import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { devicesApi, alertsApi, maintenanceApi, driversApi, groupsApi, reportsApi } from '../services/api';
import { useDevicesStore } from '../store/devices.store';
import { useTenantContextStore } from '../store/tenant-context.store';
import { connectSocket } from '../services/socket';
import MapComponent from '../components/Map';
import { formatDistanceToNow, format, subDays, startOfDay } from 'date-fns';
import { fr, de, enUS, it } from 'date-fns/locale';

const DATE_LOCALES: Record<string, typeof fr> = { fr, de, en: enUS, it };
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Truck,
  Activity,
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Clock,
  MapPin,
  Wrench,
  Users,
  Bell,
  Gauge,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FolderOpen,
  Zap,
  BarChart3,
  PieChart as PieChartIcon,
  RefreshCw,
  Fuel,
  Route,
  Timer,
} from 'lucide-react';
import clsx from 'clsx';

// Chart colors
const COLORS = {
  cyan: '#22d3ee',
  purple: '#a855f7',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  blue: '#3b82f6',
  pink: '#ec4899',
};

const PIE_COLORS = [COLORS.cyan, COLORS.purple, COLORS.green, COLORS.amber, COLORS.pink];

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0] ?? 'fr'] ?? fr;
  const { devices, setDevices, setLoading, setError } = useDevicesStore();
  const { selectedTenant } = useTenantContextStore();

  // Tenant ID for query keys - forces refetch when tenant changes
  const tenantKey = selectedTenant?.id || 'default';

  // Fetch devices
  const { isLoading, data, error, refetch: refetchDevices } = useQuery({
    queryKey: ['devices', tenantKey],
    queryFn: async () => {
      const response = await devicesApi.list({ limit: 1000 });
      return response.data;
    },
  });

  // Fetch alerts stats
  const { data: alertsStats, refetch: refetchAlerts } = useQuery({
    queryKey: ['alerts-stats', tenantKey],
    queryFn: async () => {
      const response = await alertsApi.getStats();
      return response.data;
    },
  });

  // Fetch recent alerts
  const { data: recentAlerts } = useQuery({
    queryKey: ['alerts-recent', tenantKey],
    queryFn: async () => {
      const response = await alertsApi.list({ limit: 5, status: 'ACTIVE' });
      return response.data;
    },
  });

  // Fetch maintenance stats
  const { data: maintenanceStats } = useQuery({
    queryKey: ['maintenance-stats', tenantKey],
    queryFn: async () => {
      const response = await maintenanceApi.getStats();
      return response.data;
    },
  });

  // Fetch upcoming maintenance
  const { data: upcomingMaintenance } = useQuery({
    queryKey: ['maintenance-upcoming', tenantKey],
    queryFn: async () => {
      const response = await maintenanceApi.getSchedules({ status: 'SCHEDULED', limit: 5 });
      return response.data;
    },
  });

  // Fetch drivers stats
  const { data: driversStats } = useQuery({
    queryKey: ['drivers-stats', tenantKey],
    queryFn: async () => {
      const response = await driversApi.getStats();
      return response.data;
    },
  });

  // Fetch driver scoring summary
  const { data: scoringData } = useQuery({
    queryKey: ['driver-scores-summary', tenantKey],
    queryFn: async () => {
      const response = await driversApi.getScores({ limit: 1 });
      return response.data?.summary;
    },
  });

  // Fetch groups for distribution
  const { data: groupsData } = useQuery({
    queryKey: ['groups', tenantKey],
    queryFn: () => groupsApi.list(),
  });
  const groups = groupsData?.data?.data || [];

  // Fetch daily summary (distance, fuel, driving hours)
  const todayStart = useMemo(() => startOfDay(new Date()).toISOString(), []);
  const { data: dailySummaryData } = useQuery({
    queryKey: ['daily-summary', tenantKey, todayStart],
    queryFn: async () => {
      const response = await reportsApi.getSummary({
        startDate: todayStart,
        endDate: new Date().toISOString(),
      });
      return response.data;
    },
  });

  // Fetch trips for last 7 days (for fleet activity chart)
  const weekStart = useMemo(() => startOfDay(subDays(new Date(), 6)).toISOString(), []);
  const { data: weeklyTripsData } = useQuery({
    queryKey: ['weekly-trips', tenantKey, weekStart],
    queryFn: async () => {
      const response = await reportsApi.getTrips({
        startDate: weekStart,
        endDate: new Date().toISOString(),
      });
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // cache 10 min
  });

  // Compute daily KPIs from summary
  const dailyKpis = useMemo(() => {
    const summaries: any[] = dailySummaryData?.data || [];
    const totalDistance = summaries.reduce((sum: number, s: any) => sum + (s.distance || 0), 0);
    const totalMovingMs = summaries.reduce((sum: number, s: any) => sum + (s.movingDuration || 0), 0);
    const totalFuel = summaries.reduce((sum: number, s: any) => sum + (s.fuelConsumed || 0), 0);
    const totalEnergy = summaries.reduce((sum: number, s: any) => sum + (s.energyConsumed || 0), 0);
    const totalMinutes = totalMovingMs / 60000;
    return {
      totalDistance: Math.round(totalDistance * 10) / 10,
      totalDrivingMinutes: Math.round(totalMinutes),
      totalDrivingHours: Math.round((totalMovingMs / 3600000) * 10) / 10,
      totalFuel: Math.round(totalFuel * 10) / 10,
      totalEnergy: Math.round(totalEnergy * 10) / 10,
    };
  }, [dailySummaryData]);

  // Update store when data changes
  useEffect(() => {
    if (data?.data) {
      setDevices(data.data);
    }
  }, [data, setDevices]);

  // Update error in store
  useEffect(() => {
    if (error) {
      setError((error as Error).message);
    }
  }, [error, setError]);

  // Connect to WebSocket
  useEffect(() => {
    try {
      connectSocket();
    } catch (err) {
      console.error('Failed to connect WebSocket:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  // Calculate stats
  const safeDevices = Array.isArray(devices) ? devices : [];
  const deviceStats = useMemo(() => ({
    total: safeDevices.length,
    online: safeDevices.filter((d) => d.status === 'ONLINE').length,
    offline: safeDevices.filter((d) => d.status === 'OFFLINE').length,
    moving: safeDevices.filter((d) => d.lastPosition && d.lastPosition.speed > 5).length,
    idle: safeDevices.filter((d) => d.status === 'ONLINE' && d.lastPosition && d.lastPosition.speed <= 5).length,
  }), [safeDevices]);

  // Fleet activity data from real trips (last 7 days)
  const fleetActivityData = useMemo(() => {
    const trips: any[] = weeklyTripsData?.data || [];
    // Group trips by day
    const dayMap = new Map<string, { distance: number; trips: number }>();
    for (const trip of trips) {
      const dayKey = format(new Date(trip.startTime), 'yyyy-MM-dd');
      const existing = dayMap.get(dayKey) || { distance: 0, trips: 0 };
      existing.distance += trip.distance || 0;
      existing.trips += 1;
      dayMap.set(dayKey, existing);
    }

    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dayKey = format(date, 'yyyy-MM-dd');
      const dayData = dayMap.get(dayKey) || { distance: 0, trips: 0 };
      days.push({
        name: format(date, 'EEE', { locale: dateLocale }),
        fullDate: format(date, 'd MMM', { locale: dateLocale }),
        distance: Math.round(dayData.distance * 10) / 10,
        trips: dayData.trips,
      });
    }
    return days;
  }, [weeklyTripsData]);

  // Speed distribution data
  const speedDistributionData = useMemo(() => {
    const ranges = [
      { range: '0', count: 0 },
      { range: '1-30', count: 0 },
      { range: '31-60', count: 0 },
      { range: '61-90', count: 0 },
      { range: '90+', count: 0 },
    ];

    safeDevices.forEach((d) => {
      const speed = d.lastPosition?.speed || 0;
      if (speed === 0) ranges[0].count++;
      else if (speed <= 30) ranges[1].count++;
      else if (speed <= 60) ranges[2].count++;
      else if (speed <= 90) ranges[3].count++;
      else ranges[4].count++;
    });

    return ranges;
  }, [safeDevices]);

  // Group distribution data
  const groupDistributionData = useMemo(() => {
    const distribution: { name: string; value: number; color: string }[] = [];
    const groupMap = new Map<string, { name: string; color: string; count: number }>();

    safeDevices.forEach((d) => {
      const groupId = d.group?.id || 'no-group';
      const groupName = d.group?.name || 'Sans groupe';
      const groupColor = d.group?.color || '#64748b';

      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, { name: groupName, color: groupColor, count: 0 });
      }
      groupMap.get(groupId)!.count++;
    });

    groupMap.forEach((value) => {
      distribution.push({
        name: value.name,
        value: value.count,
        color: value.color,
      });
    });

    return distribution.sort((a, b) => b.value - a.value).slice(0, 5);
  }, [devices]);

  // KPIs
  const kpis = useMemo(() => {
    const avgSpeed = calculateAverageSpeed(safeDevices);
    const onlinePercent = deviceStats.total > 0
      ? Math.round((deviceStats.online / deviceStats.total) * 100)
      : 0;
    const movingPercent = deviceStats.online > 0
      ? Math.round((deviceStats.moving / deviceStats.online) * 100)
      : 0;

    return {
      avgSpeed,
      onlinePercent,
      movingPercent,
    };
  }, [safeDevices, deviceStats]);

  const handleRefresh = () => {
    refetchDevices();
    refetchAlerts();
  };

  const alerts = recentAlerts?.data || [];
  const maintenance = upcomingMaintenance?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-500 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 border border-gray-200 text-gray-600 hover:text-white hover:border-gray-300 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="text-sm">{t('dashboard.refresh')}</span>
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Clock className="h-4 w-4" />
            <span>{t('dashboard.live')}</span>
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          </div>
        </div>
      </div>

      {/* Main KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={t('dashboard.kpi_online_vehicles')}
          value={deviceStats.online}
          total={deviceStats.total}
          icon={Truck}
          color="cyan"
          trend={kpis.onlinePercent > 70 ? 'up' : 'down'}
          trendValue={`${kpis.onlinePercent}%`}
          href="/devices"
        />
        <KPICard
          title={t('dashboard.kpi_moving')}
          value={deviceStats.moving}
          total={deviceStats.online}
          icon={Activity}
          color="purple"
          trend={kpis.movingPercent > 50 ? 'up' : 'neutral'}
          trendValue={`${kpis.movingPercent}%`}
          href="/map"
        />
        <KPICard
          title={t('dashboard.kpi_active_alerts')}
          value={alertsStats?.active || 0}
          icon={AlertTriangle}
          color="amber"
          subValue={alertsStats?.critical ? t('dashboard.kpi_critical_count', { count: alertsStats.critical }) : undefined}
          href="/alerts"
        />
        <KPICard
          title={t('dashboard.kpi_maintenance')}
          value={maintenanceStats?.overdue || 0}
          icon={Wrench}
          color={maintenanceStats?.overdue > 0 ? 'red' : 'green'}
          subValue={maintenanceStats?.upcoming ? t('dashboard.kpi_upcoming_count', { count: maintenanceStats.upcoming }) : t('dashboard.kpi_all_uptodate')}
          href="/maintenance"
        />
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <MiniKPI
          label={t('dashboard.mini_avg_speed')}
          value={kpis.avgSpeed}
          suffix="km/h"
          icon={Gauge}
          color="cyan"
        />
        <MiniKPI
          label={t('dashboard.mini_distance_today')}
          value={dailyKpis.totalDistance}
          suffix="km"
          icon={Route}
          color="purple"
          href="/reports"
        />
        <MiniKPI
          label={t('dashboard.mini_fuel')}
          value={dailyKpis.totalFuel}
          suffix="L"
          icon={Fuel}
          color="amber"
          href="/reports"
        />
        <MiniKPI
          label={t('dashboard.mini_ev_consumption')}
          value={dailyKpis.totalEnergy}
          suffix="kWh"
          icon={Zap}
          color="green"
          href="/reports"
        />
        <MiniKPI
          label={t('dashboard.mini_drive_time')}
          value={dailyKpis.totalDrivingMinutes < 60 ? dailyKpis.totalDrivingMinutes : dailyKpis.totalDrivingHours}
          suffix={dailyKpis.totalDrivingMinutes < 60 ? 'min' : 'h'}
          icon={Timer}
          color="green"
          href="/reports"
        />
        <MiniKPI
          label={t('dashboard.mini_active_drivers')}
          value={driversStats?.activeDrivers || 0}
          total={driversStats?.totalDrivers || 0}
          icon={Users}
          href="/drivers"
        />
        <MiniKPI
          label={t('dashboard.mini_driving_score')}
          value={scoringData?.avgScore || 0}
          suffix="/100"
          icon={Gauge}
          color="green"
          href="/drivers/scores"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fleet Activity Chart */}
        <div className="lg:col-span-2 glass-card overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <BarChart3 className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{t('dashboard.fleet_activity_title')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.fleet_activity_subtitle')}</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={250} minWidth={0}>
              <AreaChart data={fleetActivityData}>
                <defs>
                  <linearGradient id="colorDistance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.cyan} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.cyan} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorTrips" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="name"
                  stroke="#64748b"
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <YAxis
                  yAxisId="distance"
                  stroke="#64748b"
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <YAxis
                  yAxisId="trips"
                  orientation="right"
                  stroke="#64748b"
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  labelStyle={{ color: '#f1f5f9' }}
                  itemStyle={{ color: '#f1f5f9' }}
                  formatter={(value: any, name: any) => [
                    name === 'Distance' ? `${value} km` : `${value}`,
                    name,
                  ]}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '10px' }}
                  formatter={(value) => <span style={{ color: '#94a3b8' }}>{value}</span>}
                />
                <Area
                  yAxisId="distance"
                  type="monotone"
                  dataKey="distance"
                  name="Distance"
                  stroke={COLORS.cyan}
                  fillOpacity={1}
                  fill="url(#colorDistance)"
                />
                <Area
                  yAxisId="trips"
                  type="monotone"
                  dataKey="trips"
                  name="Trajets"
                  stroke={COLORS.purple}
                  fillOpacity={1}
                  fill="url(#colorTrips)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Group Distribution */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <PieChartIcon className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{t('dashboard.groups_distribution_title')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.groups_count', { count: groups.length })}</p>
              </div>
            </div>
          </div>
          <div className="p-4 flex items-center justify-center">
            {groupDistributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250} minWidth={0}>
                <PieChart>
                  <Pie
                    data={groupDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {groupDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '12px',
                    }}
                    labelStyle={{ color: '#f1f5f9' }}
                    itemStyle={{ color: '#f1f5f9' }}
                    formatter={(value, _name, props) => [
                      `${value ?? 0} ${t('dashboard.vehicles_chart_label')}`,
                      (props as { payload: { name: string } }).payload.name,
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center">
                <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('dashboard.no_group_configured')}</p>
              </div>
            )}
          </div>
          {groupDistributionData.length > 0 && (
            <div className="px-4 pb-4 space-y-2">
              {groupDistributionData.slice(0, 4).map((group, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: group.color || PIE_COLORS[i] }}
                    />
                    <span className="text-gray-600 truncate max-w-[120px]">{group.name}</span>
                  </div>
                  <span className="text-gray-500">{group.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Map and Speed Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 glass-card overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary-500/10">
                <MapPin className="h-5 w-5 text-primary-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{t('dashboard.live_map_title')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.active_vehicles', { count: deviceStats.online })}</p>
              </div>
            </div>
            <Link
              to="/map"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-primary-400 hover:bg-primary-500/10 transition-colors"
            >
              {t('dashboard.fullscreen')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="h-[350px]">
            <MapComponent devices={devices} showControls={false} />
          </div>
        </div>

        {/* Speed Distribution */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Zap className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{t('dashboard.speed_distribution_title')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.live')}</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={250} minWidth={0}>
              <BarChart data={speedDistributionData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#64748b"
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <YAxis
                  dataKey="range"
                  type="category"
                  stroke="#64748b"
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                  width={50}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '12px',
                  }}
                  labelStyle={{ color: '#f1f5f9' }}
                  itemStyle={{ color: '#f1f5f9' }}
                  formatter={(value) => [`${value ?? 0} ${t('dashboard.vehicles_chart_label')}`, t('dashboard.vehicles_label')]}
                  labelFormatter={(label) => `${label} km/h`}
                />
                <Bar
                  dataKey="count"
                  fill={COLORS.cyan}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{t('dashboard.stopped_count', { count: speedDistributionData[0].count })}</span>
              <span>{t('dashboard.moving_count', { count: deviceStats.moving })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: Alerts + Maintenance + Vehicles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent alerts */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Bell className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{t('dashboard.recent_alerts_title')}</h2>
                <p className="text-xs text-gray-500">
                  {t('dashboard.alerts_active', { count: alertsStats?.active || 0 })}
                </p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-white/5 max-h-[320px] overflow-y-auto">
            {alerts.length > 0 ? (
              alerts.map((alert: any) => {
                const createdAt = alert.createdAt ? new Date(alert.createdAt) : null;
                const timeAgo = createdAt && !isNaN(createdAt.getTime())
                  ? formatDistanceToNow(createdAt, { addSuffix: true, locale: dateLocale })
                  : t('dashboard.alert_unknown_date');
                return (
                  <AlertItem
                    key={alert.id}
                    message={alert.message || getAlertMessage(alert, t)}
                    vehicle={alert.device?.name || t('dashboard.fallback_vehicle_unknown')}
                    time={timeAgo}
                    severity={getSeverityLevel(alert.severity)}
                  />
                );
              })
            ) : (
              <div className="p-8 text-center">
                <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500/50 mb-3" />
                <p className="text-gray-500 text-sm">{t('dashboard.no_active_alert')}</p>
              </div>
            )}
          </div>
          <div className="p-4 border-t border-white/5">
            <Link
              to="/alerts"
              className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm text-primary-400 hover:bg-primary-500/10 transition-colors"
            >
              {t('dashboard.see_all_alerts')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Upcoming Maintenance */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-cyan/10">
                <Wrench className="h-5 w-5 text-accent-cyan" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{t('dashboard.upcoming_maintenance_title')}</h2>
                <p className="text-xs text-gray-500">
                  {t('dashboard.maintenance_planned', { count: maintenanceStats?.upcoming || 0 })}
                </p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-white/5 max-h-[320px] overflow-y-auto">
            {maintenance.length > 0 ? (
              maintenance.map((item: any) => (
                <MaintenanceItem
                  key={item.id}
                  type={item.type?.name || t('dashboard.fallback_maintenance')}
                  vehicle={item.device?.name || t('dashboard.fallback_vehicle')}
                  dueDate={item.dueDate}
                  priority={item.priority}
                />
              ))
            ) : (
              <div className="p-8 text-center">
                <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500/50 mb-3" />
                <p className="text-gray-500 text-sm">{t('dashboard.no_maintenance_planned')}</p>
              </div>
            )}
          </div>
          <div className="p-4 border-t border-white/5">
            <Link
              to="/maintenance"
              className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm text-primary-400 hover:bg-primary-500/10 transition-colors"
            >
              {t('dashboard.manage_maintenance')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Vehicle status overview */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-purple/10">
                <Truck className="h-5 w-5 text-accent-purple" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{t('dashboard.fleet_state_title')}</h2>
                <p className="text-xs text-gray-500">{t('dashboard.vehicles_total', { count: deviceStats.total })}</p>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {/* Status breakdown */}
            <div className="space-y-3">
              <StatusBar
                label={t('dashboard.label_online')}
                value={deviceStats.online}
                total={deviceStats.total}
                color="emerald"
              />
              <StatusBar
                label={t('dashboard.label_moving')}
                value={deviceStats.moving}
                total={deviceStats.total}
                color="cyan"
              />
              <StatusBar
                label={t('dashboard.label_stopped')}
                value={deviceStats.idle}
                total={deviceStats.total}
                color="purple"
              />
              <StatusBar
                label={t('dashboard.label_offline')}
                value={deviceStats.offline}
                total={deviceStats.total}
                color="gray"
              />
            </div>

            {/* Quick stats */}
            <div className="pt-4 border-t border-white/5 grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{kpis.avgSpeed}</p>
                <p className="text-xs text-gray-500">{t('dashboard.kmh_average')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-cyan-400">{kpis.movingPercent}%</p>
                <p className="text-xs text-gray-500">{t('dashboard.in_activity')}</p>
              </div>
            </div>
          </div>
          <div className="p-4 border-t border-white/5">
            <Link
              to="/devices"
              className="flex items-center justify-center gap-2 py-2 rounded-lg text-sm text-primary-400 hover:bg-primary-500/10 transition-colors"
            >
              {t('dashboard.see_all_vehicles')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="glass-card overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent-purple/10">
              <Truck className="h-5 w-5 text-accent-purple" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">{t('dashboard.recent_vehicles_title')}</h2>
              <p className="text-xs text-gray-500">{t('dashboard.online_over_total', { online: deviceStats.online, total: deviceStats.total })}</p>
            </div>
          </div>
          <Link
            to="/devices"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-primary-400 hover:bg-primary-500/10 transition-colors"
          >
            {t('dashboard.see_all')}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="table-dark">
            <thead>
              <tr>
                <th>{t('dashboard.th_vehicle')}</th>
                <th>{t('dashboard.th_status')}</th>
                <th>{t('dashboard.th_speed')}</th>
                <th className="hidden md:table-cell">{t('dashboard.th_ignition')}</th>
                <th className="hidden md:table-cell">{t('dashboard.th_group')}</th>
                <th className="hidden md:table-cell">{t('dashboard.th_last_position')}</th>
              </tr>
            </thead>
            <tbody>
              {safeDevices.slice(0, 6).map((device) => (
                <tr key={device.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: device.markerColor || '#0ea5e9' }}
                      />
                      <div>
                        <div className="font-medium text-gray-900">{device.name}</div>
                        {device.plate && (
                          <div className="text-xs text-gray-500">{device.plate}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {device.status === 'ONLINE' ? (
                      <span className="badge-online">
                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-1.5" />
                        {t('dashboard.label_online')}
                      </span>
                    ) : (
                      <span className="badge-offline">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-1.5" />
                        {t('dashboard.label_offline')}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <TrendingUp className={`h-4 w-4 ${
                        (device.lastPosition?.speed ?? 0) > 0 ? 'text-primary-400' : 'text-gray-400'
                      }`} />
                      <span className={(device.lastPosition?.speed ?? 0) > 0 ? 'text-gray-900' : 'text-gray-500'}>
                        {device.lastPosition
                          ? `${Math.round(device.lastPosition.speed)} km/h`
                          : '-'}
                      </span>
                    </div>
                  </td>
                  <td className="hidden md:table-cell">
                    {device.lastPosition?.ignition !== undefined ? (
                      device.lastPosition.ignition ? (
                        <span className="badge-online">{t('dashboard.ignition_on')}</span>
                      ) : (
                        <span className="badge-offline">{t('dashboard.ignition_off')}</span>
                      )
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="hidden md:table-cell">
                    {device.group ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: device.group.color }}
                        />
                        <span className="text-gray-600 text-sm">{device.group.name}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="hidden md:table-cell">
                    <span className="text-xs text-gray-500">
                      {device.lastPositionAt
                        ? formatDistanceToNow(new Date(device.lastPositionAt), { addSuffix: true, locale: dateLocale })
                        : '-'}
                    </span>
                  </td>
                </tr>
              ))}
              {safeDevices.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center gap-3">
                      <Truck className="h-12 w-12 text-gray-300" />
                      <p className="text-gray-500">{t('dashboard.no_vehicle_configured')}</p>
                      <Link to="/devices" className="btn-glow text-sm">
                        {t('dashboard.add_vehicle')}
                      </Link>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function calculateAverageSpeed(devices: any[]): number {
  const movingDevices = devices.filter((d) => d.lastPosition && d.lastPosition.speed > 0);
  if (movingDevices.length === 0) return 0;
  const totalSpeed = movingDevices.reduce((sum, d) => sum + (d.lastPosition?.speed || 0), 0);
  return Math.round(totalSpeed / movingDevices.length);
}

function getAlertMessage(alert: any, t: (key: string, vars?: Record<string, string | number>) => string): string {
  switch (alert.type) {
    case 'OVERSPEED':
      return t('dashboard.alert_msg_overspeed', { speed: alert.data?.speed || '?' });
    case 'GEOFENCE_ENTER':
      return t('dashboard.alert_msg_geofence_enter', { zone: alert.data?.geofenceName || t('dashboard.alert_zone_default') });
    case 'GEOFENCE_EXIT':
      return t('dashboard.alert_msg_geofence_exit', { zone: alert.data?.geofenceName || t('dashboard.alert_zone_default') });
    case 'DEVICE_OFFLINE':
      return t('dashboard.alert_msg_device_offline');
    case 'LOW_BATTERY':
      return t('dashboard.alert_msg_low_battery', { percent: alert.data?.battery || '?' });
    case 'SOS':
      return t('dashboard.alert_msg_sos');
    case 'HARSH_BRAKING':
      return t('dashboard.alert_msg_harsh_braking');
    case 'HARSH_ACCELERATION':
      return t('dashboard.alert_msg_harsh_acceleration');
    default:
      return alert.type || t('dashboard.alert_default');
  }
}

function getSeverityLevel(severity: string): 'warning' | 'danger' | 'info' {
  switch (severity) {
    case 'CRITICAL':
    case 'HIGH':
      return 'danger';
    case 'MEDIUM':
      return 'warning';
    default:
      return 'info';
  }
}

// Components
function KPICard({
  title,
  value,
  total,
  icon: Icon,
  color,
  subValue,
  trend,
  trendValue,
  href,
}: {
  title: string;
  value: number;
  total?: number;
  icon: React.ElementType;
  color: 'cyan' | 'green' | 'gray' | 'purple' | 'amber' | 'red';
  subValue?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  href?: string;
}) {
  const colorClasses = {
    cyan: { bg: 'bg-cyan-500/10', icon: 'text-cyan-400', glow: 'hover:shadow-cyan-500/10' },
    green: { bg: 'bg-emerald-500/10', icon: 'text-emerald-400', glow: 'hover:shadow-emerald-500/10' },
    gray: { bg: 'bg-gray-100', icon: 'text-gray-500', glow: '' },
    purple: { bg: 'bg-purple-500/10', icon: 'text-purple-400', glow: 'hover:shadow-purple-500/10' },
    amber: { bg: 'bg-amber-500/10', icon: 'text-amber-400', glow: 'hover:shadow-amber-500/10' },
    red: { bg: 'bg-red-500/10', icon: 'text-red-400', glow: 'hover:shadow-red-500/10' },
  };

  const classes = colorClasses[color];
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null;

  const content = (
    <>
      <div className="flex items-start justify-between">
        <div className={`p-2.5 rounded-xl ${classes.bg}`}>
          <Icon className={`h-5 w-5 ${classes.icon}`} />
        </div>
        {trend && TrendIcon && (
          <div className={clsx(
            'flex items-center gap-1 text-xs px-2 py-1 rounded-lg',
            trend === 'up' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          )}>
            <TrendIcon className="h-3 w-3" />
            {trendValue}
          </div>
        )}
      </div>
      <div className="mt-4">
        <div className="flex items-baseline gap-1">
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {total !== undefined && (
            <span className="text-lg text-gray-400">/{total}</span>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1">{title}</p>
        {subValue && (
          <p className="text-xs text-gray-400 mt-1">{subValue}</p>
        )}
      </div>
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className={clsx(
          'glass-card p-4 hover:bg-white/5 transition-all hover:scale-[1.02] hover:shadow-lg',
          classes.glow
        )}
      >
        {content}
      </Link>
    );
  }

  return <div className="glass-card p-4">{content}</div>;
}

function MiniKPI({
  label,
  value,
  total,
  suffix,
  icon: Icon,
  color,
  href,
}: {
  label: string;
  value: number;
  total?: number;
  suffix?: string;
  icon: React.ElementType;
  color?: 'cyan' | 'purple' | 'amber' | 'green';
  href?: string;
}) {
  const colorClass = color ? {
    cyan: 'text-cyan-400',
    purple: 'text-purple-400',
    amber: 'text-amber-400',
    green: 'text-green-400',
  }[color] : 'text-gray-500';

  const content = (
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg bg-white/5">
        <Icon className={clsx('h-4 w-4', colorClass)} />
      </div>
      <div>
        <p className="text-lg font-semibold text-gray-900">
          {value}
          {suffix && <span className="text-sm text-gray-500 ml-1">{suffix}</span>}
          {total !== undefined && <span className="text-sm text-gray-400">/{total}</span>}
        </p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="glass-card p-3 hover:bg-white/5 transition-colors">
        {content}
      </Link>
    );
  }

  return <div className="glass-card p-3">{content}</div>;
}

function StatusBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: 'emerald' | 'cyan' | 'purple' | 'gray';
}) {
  const percent = total > 0 ? (value / total) * 100 : 0;

  const colorClasses = {
    emerald: 'bg-emerald-500',
    cyan: 'bg-cyan-500',
    purple: 'bg-purple-500',
    gray: 'bg-gray-400',
  };

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900 font-medium">{value}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all duration-500', colorClasses[color])}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function AlertItem({
  message,
  vehicle,
  time,
  severity,
}: {
  message: string;
  vehicle: string;
  time: string;
  severity: 'warning' | 'danger' | 'info';
}) {
  const severityConfig = {
    warning: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: AlertTriangle },
    danger: { bg: 'bg-red-500/10', text: 'text-red-400', icon: XCircle },
    info: { bg: 'bg-primary-500/10', text: 'text-primary-400', icon: AlertCircle },
  };

  const config = severityConfig[severity];
  const IconComponent = config.icon;

  return (
    <div className="p-4 hover:bg-white/5 transition-colors cursor-pointer">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.bg}`}>
          <IconComponent className={`h-4 w-4 ${config.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">{message}</p>
          <p className="text-xs text-gray-500 mt-0.5">{vehicle}</p>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">{time}</span>
      </div>
    </div>
  );
}

function MaintenanceItem({
  type,
  vehicle,
  dueDate,
  priority,
}: {
  type: string;
  vehicle: string;
  dueDate: string | null;
  priority: string;
}) {
  const { i18n } = useTranslation();
  const dateLocale = DATE_LOCALES[i18n.language?.split('-')[0] ?? 'fr'] ?? fr;
  const priorityConfig: Record<string, { bg: string; text: string }> = {
    URGENT: { bg: 'bg-red-500/10', text: 'text-red-400' },
    HIGH: { bg: 'bg-amber-500/10', text: 'text-amber-400' },
    MEDIUM: { bg: 'bg-primary-500/10', text: 'text-primary-400' },
    LOW: { bg: 'bg-gray-100', text: 'text-gray-500' },
  };

  const config = priorityConfig[priority] || priorityConfig.MEDIUM;

  return (
    <div className="p-4 hover:bg-white/5 transition-colors cursor-pointer">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.bg}`}>
          <Wrench className={`h-4 w-4 ${config.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900">{type}</p>
          <p className="text-xs text-gray-500 mt-0.5">{vehicle}</p>
        </div>
        {dueDate && (
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {formatDistanceToNow(new Date(dueDate), { addSuffix: true, locale: dateLocale })}
          </span>
        )}
      </div>
    </div>
  );
}
